<template>
<!-- eslint-disable max-len -->
<span>
  <transition enter-active-class="animated slideInDown" leave-active-class="animated fadeOut">
    <div class="report-container" id="report-container">
      <div :class="`report ${pdf ? 'pdf' : ''}`" ref="report" id="report">
        <div :class="`report-header ${pdf ? 'pdf' : ''}`">
          <!-- logo -->
          <img v-if="logo" :src="resolveAssetPath(logo, 'ui/customer')" :class="`logo ${pdf ? 'pdf' : ''}`" ref="logo">
          <!-- title -->
          <h1 class="report-title no-wrap">{{activeEnvironment.reportTitle || game.reportTitle || 'Report'}}</h1>
        </div>

        <!-- name/date -->
        <div :style="{
          display: 'flex',
          'flex-flow': 'row nowrap',
          'justify-content': 'space-between',
          padding: '4px 50px',
          'font-size': '1.6em',
        }">
          <div class="no-wrap">
            {{username}}
          </div>
          <div class="no-wrap">
            {{date}}
          </div>
        </div>

        <!-- divider -->
        <div
          class="primarybox"
          :style="{
            height: '3px',
          }"
        >
        </div>

        <!-- score -->
        <div
          :style="{
            padding: '8px 50px',
            'font-size': '1.6em',
          }"
        >
          Your score: <span style="font-size: 1.65em;">{{ score }}</span>
        </div>

        <!-- divider -->
        <div
          class="primarybox"
          :style="{
            height: '3px',
          }"
        >
        </div>

        <!-- static content -->
        <div :style="{
          padding: '8px 50px',
          'font-size': '1.25em',
        }">
          <div style="font-weight: bold;">What are blood sugar targets?</div>
          <br>
          <div>A blood sugar target is the range you try to stay within as much as possible. These are typical targets:</div>
          <br>
          <ul>
            <li>Before a meal: 80 to 130 mg/dL.</li>
            <li>Two hours after the start of a meal: Less than 180 mg/dL.</li>
            <li>Blood sugar below 70 mg/dL is considered low.</li>
          </ul>
          <div>Low blood sugar</div>
          <ul>
            <li>Blood sugar below 70 mg/dL is considered low. If you think you have low blood sugar, do a blood sugar check.</li>
            <br>
            <li>If you aren’t able to check it, go ahead and treat it by intaking fast-acting sources of sugar (such as sugar, regular soft drink, or fruit juice)</li>
            <br>
            <li>And then eat a long-acting source of sugar (crackers and cheese, or a sandwich with meat).</li>
            <br>
            <li>Untreated low blood sugar can be dangerous, so it’s important to know what to do about it and to treat it immediately.</li>
          </ul>
          <div>
            <a target="_blank" href="https://www.cdc.gov/diabetes/about/low-blood-sugar-hypoglycemia.html">Low blood sugar</a> (also called hypoglycemia) has many causes, including missing a meal, taking too much insulin, taking other diabetes medication, exercising more than normal, and drinking alcohol. Blood sugar below 70 mg/dL is considered low.
          </div>
          <div>Common symptoms of <span style="font-weight: bold;">low blood sugar</span> include:</div>
          <ul>
            <li>Shaking.</li>
            <li>Sweating.</li>
            <li>Nervousness or anxiety.</li>
            <li>Irritability or confusion.</li>
            <li>Dizziness.</li>
            <li>Hunger.</li>
          </ul>
          <div>
            If you think you may have low blood sugar, check it even if you don’t have symptoms. <span style="font-weight: bold;">Low blood sugar can be dangerous</span> and should be treated as soon as possible.
          </div>
          <br>
          <div>
            Reference: CDC (2022). Manage Blood Sugar.
            <a target="_blank" href="https://www.cdc.gov/diabetes/treatment/">https://www.cdc.gov/diabetes/treatment/</a>
          </div>
          <br>
          <!-- divider -->
          <div
            class="primarybox"
            :style="{
              height: '3px',
            }"
          >
          </div>
          <br>
          <div>Common symptoms of high blood sugar include:</div>
          <ul>
            <li>Feeling very tired.</li>
            <li>Feeling thirsty.</li>
            <li>Having blurry vision.</li>
            <li>Needing to urinate (pee) more often.</li>
          </ul>
          <div>
            Many things can cause high blood sugar (hyperglycemia), including being sick,
            being stressed, eating more than planned, and not giving yourself enough insulin.
            Over time, high blood sugar can lead to long-term, serious health problems.
          </div>
          <br>
          <div>
            <a target="_blank" href="https://www.cdc.gov/diabetes/living-with/managing-sick-days.html">If you get sick</a>, your blood sugar can be hard to manage. You may not be able to eat
            or drink as much as usual, which can affect blood sugar levels. If you’re ill and your
            blood sugar is 240 mg/dL or above, use an over-the-counter ketone test kit to check
            your urine for ketones and call your doctor if your ketones are high.
            <span style="font-weight: bold;">High ketones can be an early sign of diabetic ketoacidosis, which is a medical emergency and needs to be treated immediately.</span>
          </div>
          <br>
          <div>
            Reference: CDC (2022). Manage Blood Sugar.
            <a target="_blank" href="https://www.cdc.gov/diabetes/treatment/">https://www.cdc.gov/diabetes/treatment/</a>
          </div>
        </div>

        <!-- divider -->
        <div
          class="primarybox"
          :style="{
            height: '3px',
          }"
        >
        </div>

        <!-- footer message (e.g. displaying ©) -->
        <div :style="{
          padding: '2px 25px',
          width: '100%',
          'border-top': '1px solid white',
          'background-color': 'black',
          color: 'white',
        }">
          {{activeEnvironment.reportFooter}}
        </div>
      </div>
    </div>
  </transition>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <div v-if="showDownloading" class="download-message">Downloading!</div>
  </transition>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <button v-if="showNextButton"
      class="btn download-button"
      @click.prevent="onClickDownload"
    >Download</button>
  </transition>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <bottom-bar v-if="showNextButton" :next="showNarratorNotification ? onComplete : undefined" :tabindex="3" ref="bottomBar" :noBar="true">
      <span slot="button">
        <span v-html="(narrative && narrative.nextButtonText) ? narrative.nextButtonText : ((game && game.nextButtonDefaultText) ? game.nextButtonDefaultText : 'Next')"></span>
      </span>
    </bottom-bar>
  </transition>
</span>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import JsPDF from 'jspdf';
import domtoimage from 'dom-to-image-more';

import Narrator from '../ui/Narrator';
import NarratorNotification from '../ui/NarratorNotification';
import BottomBar from '../ui/BottomBar';

export default {
  // $emit 'input' on complete
  components: {
    Narrator,
    NarratorNotification,
    BottomBar,
  },
  data() {
    return {
      showNextButton: false,
      showDownloading: false,
      showNarrator: false,
      showNarratorNotification: false,
      pdf: false,
      score: 0,
    };
  },
  computed: {
    ...mapGetters([
      'activeEnvironment',
      'activeClass',
      'characters',
      'tooltip',
      'narrative',
      'lastNarrative',
      'nextNarrative',
      'narrativeNavbarText',
      'narrativeText',
      'group',
      'me',
      'metrics',
      'game',
      'dateActiveGameCompleted',
      'challenge'
    ]),
    username() {
      const {
        firstName = '',
        lastName = '',
        email = ''
      } = this.me || {};
      return firstName || lastName ? `${firstName} ${lastName}` : email;
    },
    date() {
      return (this.game.carryOverMetricsFromActive && this.dateActiveGameCompleted != null)
        ? new Date(this.dateActiveGameCompleted).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
        : ((this.challenge && this.challenge.completed)
          ? new Date(this.challenge.completed).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
          : ((this.group && this.group.updated)
            ? new Date(this.group.updated).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
            : new Date(Date.now()).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })));
    },
    reportFileName() {
      return (this.activeClass != null && this.activeClass.studentReportFileName != null)
        ? this.activeClass.studentReportFileName
        : (this.activeEnvironment.reportTitle || this.game.reportTitle || 'Report');
    },
    logo() {
      return this.activeEnvironment.reportLogo || this.activeEnvironment.creditsLogo || this.activeEnvironment.titleLogo;
    },
  },
  methods: {
    ...mapActions([
      'getMyGroup',
      'stopSounds'
    ]),
    async show() {
      this.showNarrator = true;
      await new Promise(resolve => setTimeout(resolve, 1250));
      this.showNarratorNotification = true;
      await new Promise(resolve => setTimeout(resolve, 1250));
      await Vue.nextTick();
    },
    async reset() {
      await Vue.nextTick();
      await this.show();
    },
    navigate() {
      this.$refs.bottomBar.navigate();
    },
    async onComplete() {
      if (this.tooltip && this.tooltip.preventStateProgressionWhileOpen) {
        window.dispatchEvent(new Event('click'));
        return;
      }
      this.$emit('input');
    },
    async loadPageData() {
      try {
        const { _id: groupId } = this.group;
        await this.getMyGroup({ groupId });
      } catch (err) {
        if (await this.$router.handleGameError(err)) return;
        this.$router.showGameErrorCritical();
      }
    },
    setStats() {
      console.log(this.group);
      // TODO: the following order-dependent coupling is not ideal, but it currently seems
      //       to be the best way to do this given the way the data is currently organized.
      //       An ID-based coupling or a name-based coupling seems similarly dicey,
      //       and they'd just be costlier to compute.

      if (this.group && this.group.metrics && this.group.metrics.length && this.group.metrics[0]) {
        this.score = this.group.metrics[0].value;
      }
    },
    onBeforePrint(mql) {
      if (mql instanceof Event) {
        this.pdf = true;
        setTimeout(() => {
          this.pdf = false;
        }, 250);
      } else if (mql.matches) {
        this.pdf = true;
        setTimeout(() => {
          this.pdf = false;
        }, 250);
      }
    },
    iOS() {
      const platform = navigator.platform || navigator.userAgentData.platform;
      return [
        'iPad Simulator',
        'iPhone Simulator',
        'iPod Simulator',
        'iPad',
        'iPhone',
        'iPod'
      ].includes(platform)
      // iPad on iOS 13 detection
      || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
    },
    isSafari() {
      return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    },
    async onClickDownload() {
      if (this.showDownloading) return;
      this.showDownloading = true;
      // PDF download works in all browsers except pre-chromium Edge
      // (fails at domtoimage.toPng, and no replacement seems viable, see above)
      // fix for Edge (for now): make the page printable, and open the print dialog automatically
      // (in response to domtoimage.toPng or any other part of the autodownload throwing any error)
      // The user can print to PDF or actually print. Bonus is you can print in other browsers too.
      // Edge requires the user to manually set the Scale in the print dialog to 50% for it to fit.
      // Note that this is generally no longer a problem with the new chromium-based Edge.
      this.pdf = true;
      try {
        await Vue.nextTick();
        let dataUrl = await domtoimage.toPng(this.$refs.report, { cacheBust: true });
        // redundant calls required to work on Safari... (https://github.com/tsayen/dom-to-image/issues/343)
        const brokenBecauseSafari = this.iOS() || this.isSafari();
        if (brokenBecauseSafari) {
          dataUrl = await domtoimage.toPng(this.$refs.report);
          dataUrl = await domtoimage.toPng(this.$refs.report);
        }
        const doc = new JsPDF('p', 'pt', 'a4');
        doc.addImage(dataUrl, 'PNG', 28, 50, 540, 747);

        // TODO: if no autodownload, no soft-lock (explained below), so soft-lock fix commented out
        doc.save(`${this.reportFileName}.pdf`);
        // Before we download the PDF, we have to consider the possibility that the
        // user's settings are set to view the PDF in the browser first when downloading a PDF
        // (instead of actually downloading it). This can be true in many modern browsers.
        // This is a problem because if it opens in the same page, it destroys the Vue app state.
        // This ends up soft-locking the player such that they can't get past the report page.
        // This problem came to my attention when a Firefox update changed the PDF download behavior
        // to default to viewing it in browser (on the same page) when you initiate a download.
        // To fix this while minimally impacting anyone whose browser actually just downloads stuff
        // when a download is initiated, instead first open a new tab, then artificially initiate
        // the download with a bogus DOM hyperlink element that targets the bloburl output by JsPDF,
        // which either downloads the file or loads the blob viewer. If it doesn't load the
        // blob viewer, we autoclose the tab, leaving you back on the report page. If it does load
        // the blob viewer, the user is left in the blob viewer, where they can either:
        // - close it, returning to the original report tab without downloading, where they can
        //   hit print or refresh to save off the report another way, or continue on with the game.
        // - download/print it (which presents a system dialog, and does carry over the expected
        //   default name) (and then close it to return back to the game).
        // eslint-disable-next-line max-len
        // const win = window.open(`${window.location.origin}/downloadplayerreport/${encodeURIComponent(this.reportFileName)}/${encodeURIComponent(doc.output('bloburl', { filename: `${this.reportFileName}.pdf` }))}`, '_blank');
        // if (win == null) {
        //   doc.save(`${this.reportFileName}.pdf`);
        // }

        this.pdf = false;
        this.showDownloading = false;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to download report PDF!', error);
        try {
          this.pdf = true;
          this.selectedTab = 0;
          await Vue.nextTick();
          window.print();
          this.pdf = false;
          this.showDownloading = false;
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Failed to open print dialog!', e);
          this.pdf = false;
          this.showDownloading = false;
          this.selectedTab = 0;
        }
      }
    },
  },
  watch: {
    async narrative(newValue) {
      if (newValue) await this.reset();
    },
  },
  async mounted() {
    this.setStats();
    await Vue.nextTick();
    this.stopSounds();

    // polyfill for safari but works for others as well
    const mediaQueryList = (window.matchMedia != null) ? window.matchMedia('print') : null;
    if (mediaQueryList != null && mediaQueryList.addListener != null) {
      mediaQueryList.removeListener(this.onBeforePrint);
      mediaQueryList.addListener(this.onBeforePrint);
    } else {
      // what you'd normally use, if you weren't needing to support Safari:
      window.removeEventListener('beforeprint', this.onBeforePrint);
      window.addEventListener('beforeprint', this.onBeforePrint);
    }

    await this.show();

    await new Promise((resolve, reject) => {
      if (this.$refs.logo.complete) { // wait for logo to load
        resolve();
      } else {
        this.$refs.logo.addEventListener('load', resolve);
        this.$refs.logo.addEventListener('error', reject);
      }
    });

    // uncomment to enable autodownload (but re-add soft-lock fix above if you do)
    // await this.onClickDownload();

    this.showNextButton = true;
  },
};
</script>

<style lang="scss" scoped>
@media print {
  body, body * {
    visibility: hidden !important;
    overflow: visible !important;
  }
  #report-container, #report-container * {
    visibility: visible !important;
    overflow: visible !important;
  }
  #report-container {
    left: 8.75% !important;
    top: 0 !important;
    width: 82.5% !important;
    overflow: visible !important;
    z-index: 10 !important;

    // this makes background graphics show up without needing to check the box
    color-adjust: exact !important;
    print-color-adjust: exact !important;
    -webkit-print-color-adjust: exact !important;
  }
}

.no-wrap {
  white-space: nowrap;
}

.download-button {
  position: absolute;
  bottom: 210px;
  right: 0;
  width: 350px;
  font-size: 28px;
  line-height: 55px;
  margin-right: 45px;
  z-index: 100 !important;
  transition: opacity 0.5s ease-in-out;
}

hr {
  border-top: 1px solid #e9ecef;
}
.report {
  background-color: white;
  color: black;
}
.report.pdf {
  border: solid;
  border-width: 3.5px;
  border-color: var(--primaryColor);
}
.report-container {
  position: absolute;
  width: 1150px;
  height: 850px;
  top: 30px;
  left: 30px;
  z-index: 1;
  overflow: auto;
}

.report-header {
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  height: 60px;
  padding: 0 50px;
  background-color: black;
  color: lightgrey;
}
.logo {
  object-fit: contain;
  height: 90%;
  max-width: 40%;
  z-index: 10;
}
.report-title {
  font-size: 1.96em;
  white-space: pre-wrap;
  text-align: right;
  margin: auto 0;
  max-width: 60%;
  max-height: 100%;
  line-height: 100%;
}

.primarybox {
  background-color: var(--primaryColor);
  color: white;

  color-adjust: exact;
  -webkit-print-color-adjust: exact;
}

// https://stackoverflow.com/a/31633969/10747078
br {
  display: block;
  content: '';
  margin-top: 0.95em;
}

.slideInDown {
  animation-duration: 1s;
  animation-timing-function: cubic-bezier(0.19, 1, 0.22, 1); // out expo
}
.fadeOutUp {
  animation-duration: 1s;
  animation-timing-function: cubic-bezier(0.55, 0.055, 0.675, 0.19); // in cubic
}
.narrator-notification {
  bottom: 205px;
}

.download-message {
  position: absolute;
  top: 50%;
  left: 25%;
  z-index: 1000;
  border-radius: 15px;
  border: 3px solid black;
  background-color: var(--primaryColor);
  color: white;
  font-size: 30pt;
  padding: 20px;
  font-weight: bold;
}
</style>



// WEBPACK FOOTER //
// src/components/game/narrative/NarrativeReportHPG.vue